import { DragEvent, useRef, useState } from "react";
import type { UploadFolder } from "@/types/site";
import { uploadImage, type ImageUploadProgress } from "@/lib/api";

const ACCEPTED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

type ImageDropzoneProps = {
  folder: UploadFolder;
  multiple?: boolean;
  disabled?: boolean;
  label?: string;
  onUploaded: (results: { url: string; public_id?: string }[]) => void | Promise<void>;
  onError?: (message: string) => void;
};

function apiErrorDetail(err: unknown, fallback: string): string {
  if (typeof err === "object" && err !== null && "response" in err) {
    const response = (err as { response?: { data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

export const ImageDropzone = ({
  folder,
  multiple = false,
  disabled = false,
  label = "Drag & drop images here, or click to browse",
  onUploaded,
  onError,
}: ImageDropzoneProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    total: number;
    completed: number;
    percent: number;
  } | null>(null);
  const uploadLoadedRef = useRef<number[]>([]);

  const uploadFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length || disabled) return;

    const invalid = list.find((f) => !ACCEPTED_IMAGE_TYPES.has(f.type));
    if (invalid) {
      onError?.("Only JPEG, PNG, WebP, or GIF images are allowed");
      return;
    }

    setUploading(true);
    uploadLoadedRef.current = new Array(list.length).fill(0);
    setUploadProgress({ total: list.length, completed: 0, percent: 0 });

    const reportProgress = (completed: number) => {
      const totalBytes = list.reduce((sum, file) => sum + file.size, 0);
      const loaded = uploadLoadedRef.current.reduce((sum, bytes) => sum + bytes, 0);
      const percent =
        totalBytes > 0 ? Math.min(100, Math.round((loaded / totalBytes) * 100)) : 0;
      setUploadProgress({ total: list.length, completed, percent });
    };

    try {
      let completed = 0;
      const results = await Promise.all(
        list.map((file, index) =>
          uploadImage(
            file,
            folder,
            ({ loaded, total }: ImageUploadProgress) => {
              const fileTotal = total && total > 0 ? total : file.size;
              uploadLoadedRef.current[index] = Math.min(loaded, fileTotal);
              reportProgress(completed);
            }
          ).then((result) => {
            uploadLoadedRef.current[index] = file.size;
            completed += 1;
            reportProgress(completed);
            return result;
          })
        )
      );
      await onUploaded(results.map((r) => ({ url: r.url, public_id: r.public_id })));
    } catch (err: unknown) {
      onError?.(apiErrorDetail(err, "Image upload failed"));
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading || disabled) return;
    setDragOver(true);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading || disabled || !e.dataTransfer.files?.length) return;
    void uploadFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-2">
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!uploading && !disabled) fileInputRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!uploading && !disabled) fileInputRef.current?.click();
          }
        }}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`border border-dashed p-4 transition-colors cursor-pointer ${
          dragOver
            ? "border-vg-navy bg-[#f3f4f6]"
            : "border-black/20 bg-[#f8fafc] hover:border-black/40"
        } ${uploading || disabled ? "opacity-60 pointer-events-none" : ""}`}
        data-testid="admin-image-dropzone"
      >
        <p className="text-sm text-vg-ink">
          {uploading ? "Uploading to Cloudinary…" : dragOver ? "Drop images to upload" : label}
        </p>
        <p className="text-xs text-vg-gray mt-1">
          JPEG, PNG, WebP, or GIF · max 8MB each
          {multiple ? " · multiple files supported" : ""}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple={multiple}
          disabled={uploading || disabled}
          onChange={(e) => void uploadFiles(e.target.files ?? [])}
          className="sr-only"
          data-testid="admin-image-file"
        />
      </div>

      {uploading && uploadProgress && (
        <div className="space-y-1.5 pt-1" data-testid="admin-image-upload-progress">
          <div className="flex items-center justify-between gap-3 text-xs text-vg-gray">
            <span>
              Uploading {uploadProgress.completed} of {uploadProgress.total} file
              {uploadProgress.total === 1 ? "" : "s"}
            </span>
            <span className="tabular-nums">{uploadProgress.percent}%</span>
          </div>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-black/10"
            role="progressbar"
            aria-valuenow={uploadProgress.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Image upload progress"
          >
            <div
              className="h-full rounded-full bg-vg-navy transition-[width] duration-150 ease-out"
              style={{ width: `${uploadProgress.percent}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageDropzone;
